/* eslint-disable no-mixed-spaces-and-tabs */
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { change, stopSubmit, touch } from 'redux-form';
import { notificationInit } from '../../modules/notifications/actions';
import { FORM_NAME, REQUEST_ERROR_MESSAGE, ORDERS_FORM_FIELDS_NAMES } from './constants';
import { transformToValidationErrors } from './utils';
import { DISMISS_TIME } from '../../api/constants';
import {
	CREATE_ORDER,
	DELETE_ORDER,
	GET_CUSTOMERS,
	setCustomersAction,
	setIsCustomersLoadingAction,
	GET_ORDER_INFO,
	setOrderInfoAction,
	setIsOrderDeletingAction,
	setIsOrderInfoLoadingAction,
	setIsOrderSavingAction,
	UPDATE_ORDER,
	GET_ADDRESSES,
	setAddressesAction,
	GET_MODES,
	setModesAction,
	setIsModesLoadingAction,
	GET_PARTNERS,
	setPartnersAction,
	getAddressesAction,
	setIsPartnersLoadingAction,
	setIsAddressesLoadingAction,
	getContactsAction,
	GET_CONTACTS,
	setContactsAction,
	setIsContactsLoadingAction,
} from './actions';
import {
	createOrdersRequest,
	deleteOrderDetailsRequest,
	getLegalsDictionaryRequest,
	getContactsRequest,
	getModesDictionaryRequest,
	getOrdersDetailsRequest,
	updateOrdersRequest,
} from '../../api/requests';
import {
	getAddressesSelector,
	getAddressSearchValueSelector,
	getContactSearchValueSelector,
	getContactsSelector,
	getDataForSaveSelector,
	getFormInitialValuesSelector,
	getFormIsInvalidSelector,
	getIsOrderAddFormOpenSelector,
	getIsOrderEditFormOpenSelector,
	getPartnerSearchValueSelector,
	getPartnersSelector,
	getPointsSelector,
} from './selectors';
import { getOrdersListAction } from '../orders/actions';
import { errorHandler } from '../../api/utils';
import { getPartnerAddressesRequest, getPartnersRequest } from '../../api/partners-requests';
import {
	getGroupsListAction,
	getCollectionGroupListAction,
	getCollectionWayListAction,
	getWaysGroupListAction,
	getOrdersCollectionWayListAction,
	getOrdersCollectionGroupListAction,
} from '../orders-groups/actions';
import {
	getCollectionGroupOpenSelector,
	getCollectionWayOpenSelector,
	getGroupOpenSelector,
	getGroupWayOpenSelector,
	getWayOpenSelector,
} from '../orders-groups/selectors';

export function* getOrderInfoSaga({ id }) {
	try {
		yield put(setIsOrderInfoLoadingAction(true));

		const isOrderAddFormOpen = yield select(getIsOrderAddFormOpenSelector());

		const isOrderEditFormOpen = yield select(getIsOrderEditFormOpenSelector());

		const { data, message, toast } = yield call(getOrdersDetailsRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}
			yield put(setOrderInfoAction(data));

			if (isOrderAddFormOpen || isOrderEditFormOpen) {
				const addresses = data.points.reduce((acc, point, index) => {
					acc[index] = point.address ? [point.address] : [];
					return acc;
				}, {});

				for (let i = 0; i < data.points?.length || 0; i++) {
					if (data.points?.[i]?.partner?.id) {
						yield put(getAddressesAction({ id: data.points[i].partner.id, index: i }));
					}
				}

				yield put(setAddressesAction(addresses));

				const contacts = data.points.reduce((acc, point, index) => {
					acc[index] = point.contact
						? [
								{
									...point.contact,
									name: `${point.contact.last_name || ''} ${point.contact.first_name || ''} ${
										point.contact.middle_name || ''
									}`,
								},
						  ]
						: [];
					return acc;
				}, {});

				for (let i = 0; i < data.points?.length || 0; i++) {
					if (data.points?.[i]?.address?.id) {
						yield put(
							getContactsAction({ id: data.points[i].address.id, index: i, notChangeDefault: true }),
						);
					}
				}

				yield put(setContactsAction(contacts));
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrderInfoLoadingAction(false));
	}
}

export function* getCustomersSaga() {
	try {
		yield put(setIsCustomersLoadingAction(true));

		const { data, message, toast } = yield call(getLegalsDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setCustomersAction(
					data.map(customer => ({
						key: customer.id,
						value: customer.id,
						text: customer.name,
					})),
				),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsCustomersLoadingAction(false));
	}
}

export function* getModesSaga() {
	try {
		yield put(setIsModesLoadingAction(true));

		const { data, message, toast } = yield call(getModesDictionaryRequest, '?receive=all');

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			yield put(
				setModesAction([...data.map(item => ({ key: item.id, value: item.id, text: item.name_with_symbol }))]),
			);
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsModesLoadingAction(false));
	}
}

export function* deleteOrderSaga({ payload: { id, redirect, searchQuery, pathname } }) {
	try {
		yield put(setIsOrderDeletingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const collectionGroupOpen = yield select(getCollectionGroupOpenSelector());

		const collectionWayOpen = yield select(getCollectionWayOpenSelector());

		const { data, message, toast } = yield call(deleteOrderDetailsRequest, id);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			redirect();

			if (pathname.includes('orders_groups')) {
				yield put(getGroupsListAction({ searchQuery, noLoading: true }));

				if (data.way) {
					if (groupWayOpen) {
						yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
					}

					if (wayOpen) {
						yield put(
							getCollectionWayListAction({
								row: wayOpen,
								searchQuery,
							}),
						);

						if (collectionWayOpen) {
							yield put(
								getOrdersCollectionWayListAction({
									row: wayOpen,
									collection: collectionWayOpen,
									searchQuery,
								}),
							);
						}
					}
				} else if (data.group && groupOpen) {
					yield put(
						getCollectionGroupListAction({
							row: groupOpen,
							searchQuery,
						}),
					);

					if (collectionGroupOpen) {
						yield put(
							getOrdersCollectionGroupListAction({
								row: groupOpen,
								collection: collectionGroupOpen,
								searchQuery,
							}),
						);
					}
				}
			} else {
				yield put(getOrdersListAction({ searchQuery, noLoading: true }));
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrderDeletingAction(false));
	}
}

export function* createOrderSaga({ payload: { redirect, searchQuery } }) {
	try {
		yield put(setIsOrderSavingAction(true));

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(createOrdersRequest, dataForSave);

		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: toast?.link } : {}),
					}),
				);
			}

			redirect();
			yield put(getOrdersListAction({ searchQuery, noLoading: true }));
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...ORDERS_FORM_FIELDS_NAMES));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrderSavingAction(false));
	}
}

export function* updateOrderSaga({ payload: { id, redirect, searchQuery, pathname } }) {
	try {
		yield put(setIsOrderSavingAction(true));

		const groupOpen = yield select(getGroupOpenSelector());

		const groupWayOpen = yield select(getGroupWayOpenSelector());

		const wayOpen = yield select(getWayOpenSelector());

		const collectionGroupOpen = yield select(getCollectionGroupOpenSelector());

		const collectionWayOpen = yield select(getCollectionWayOpenSelector());

		const isInvalid = yield select(getFormIsInvalidSelector());

		if (isInvalid) return;

		const dataForSave = yield select(getDataForSaveSelector());

		const { data, errors, message, toast } = yield call(updateOrdersRequest, id, dataForSave);
		if (data) {
			if (toast) {
				yield put(
					notificationInit({
						id: uuidv4(),
						dismissAfter: DISMISS_TIME,
						...toast,
						...(toast?.link ? { link: toast?.link } : {}),
					}),
				);
			}

			redirect();

			yield put(setOrderInfoAction(data));

			if (pathname.includes('orders_groups')) {
				yield put(getGroupsListAction({ searchQuery, noLoading: true }));

				if (data.way) {
					if (groupWayOpen) {
						yield put(getWaysGroupListAction({ row: groupWayOpen, searchQuery, noLoading: true }));
					}

					if (wayOpen) {
						yield put(
							getCollectionWayListAction({
								row: wayOpen,
								searchQuery,
							}),
						);

						if (collectionWayOpen) {
							yield put(
								getOrdersCollectionWayListAction({
									row: wayOpen,
									collection: collectionWayOpen,
									searchQuery,
								}),
							);
						}
					}
				} else if (data.group && groupOpen) {
					yield put(
						getCollectionGroupListAction({
							row: groupOpen,
							searchQuery,
						}),
					);

					if (collectionGroupOpen) {
						yield put(
							getOrdersCollectionGroupListAction({
								row: groupOpen,
								collection: collectionGroupOpen,
								searchQuery,
							}),
						);
					}
				}
			} else {
				yield put(getOrdersListAction({ searchQuery, noLoading: true }));
			}
		} else {
			if (errors) {
				yield put(touch(FORM_NAME, ...ORDERS_FORM_FIELDS_NAMES));
				yield put(stopSubmit(FORM_NAME, transformToValidationErrors(errors)));
			}

			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsOrderSavingAction(false));
	}
}

export function* getPartnersSaga({ payload: index }) {
	try {
		yield put(setIsPartnersLoadingAction(true));

		const query = yield select(getPartnerSearchValueSelector());

		const { data, message, toast } = yield call(
			getPartnersRequest,
			`${query[index] ? `?search=${query[index]}&receive=all` : '?receive=all'}`,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			if (index !== undefined) {
				const partners = yield select(getPartnersSelector());

				yield put(setPartnersAction({ ...partners, [index]: data }));
			} else {
				const points = yield select(getPointsSelector());

				const newPartners = points.reduce((acc, _, index) => {
					acc[index] = data;
					return acc;
				}, {});

				yield put(setPartnersAction(newPartners));
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsPartnersLoadingAction(false));
	}
}

export function* getAddressesSaga({ payload: { id, index } }) {
	try {
		yield put(setIsAddressesLoadingAction({ [index]: true }));

		const query = yield select(getAddressSearchValueSelector());

		const { data, message, toast } = yield call(
			getPartnerAddressesRequest,
			`${query[index] ? `?search=${query[index]}&receive=all` : '?receive=all'}`,
			id,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const addresses = yield select(getAddressesSelector());

			yield put(setAddressesAction({ ...addresses, [index]: data }));
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsAddressesLoadingAction({ [index]: false }));
	}
}

export function* getContactsSaga({ payload: { id, index, notChangeDefault } }) {
	try {
		yield put(setIsContactsLoadingAction({ [index]: true }));

		const query = yield select(getContactSearchValueSelector());

		const { data, message, toast } = yield call(
			getContactsRequest,
			`${query[index] ? `?search=${query[index]}&receive=all` : '?receive=all'}`,
			id,
		);

		if (data) {
			if (toast) {
				yield put(notificationInit({ id: uuidv4(), dismissAfter: DISMISS_TIME, ...toast }));
			}

			const contacts = yield select(getContactsSelector());

			yield put(
				setContactsAction({
					...contacts,
					[index]: data?.map(contact => ({
						...contact,
						name: `${contact.last_name || ''} ${contact.first_name || ''} ${contact.middle_name || ''}`,
					})),
				}),
			);

			const defaultContact = data.find(contact => contact.is_default);

			if (defaultContact && !notChangeDefault) {
				const info = yield select(getFormInitialValuesSelector());

				const newPoints = [...(info.points || [])];

				newPoints[index] = {
					...(info.points?.[index] || {}),
					contact: defaultContact,
				};

				yield put(change(FORM_NAME, `points.${index}.contact`, defaultContact));
			}
		} else {
			throw new Error(message || REQUEST_ERROR_MESSAGE);
		}
	} catch (error) {
		yield fork(errorHandler, error);
	} finally {
		yield put(setIsContactsLoadingAction({ [index]: false }));
	}
}

export default function* trailersContainerSaga() {
	yield takeEvery(GET_ORDER_INFO, getOrderInfoSaga);
	yield takeEvery(GET_CUSTOMERS, getCustomersSaga);
	yield takeEvery(GET_MODES, getModesSaga);
	yield takeEvery(GET_PARTNERS, getPartnersSaga);
	yield takeEvery(GET_ADDRESSES, getAddressesSaga);
	yield takeEvery(GET_CONTACTS, getContactsSaga);
	yield takeEvery(DELETE_ORDER, deleteOrderSaga);
	yield takeEvery(CREATE_ORDER, createOrderSaga);
	yield takeEvery(UPDATE_ORDER, updateOrderSaga);
}
